import React from 'react';
import { cn } from '@express-labs/raven-tools';
import Image from '../../../Image/Image';
import Price from '../../../Price/Price';
import StarRating from '../../../StarRating/StarRating';
import ProductTag from '../shared/ProductTag/ProductTag';
import ProductTitle from '../shared/ProductTitle/ProductTitle';
import ProductSku from '../shared/ProductSku/ProductSku';
import ProductColors from '../shared/ProductColors/ProductColors';
import ProductShipping from '../shared/ProductShipping/ProductShipping';
import ProductDetail from '../shared/ProductDetail/ProductDetail';
import { HorizontalProductCardProps } from '../../productCardTypes';
import s from './HorizontalProductCard.scss';

const HorizontalProductCard = ({
  additionalColorsLink,
  canUseRouter = true,
  children,
  className,
  colorClassName,
  colorName,
  colors = {},
  customText = '',
  descriptionWrapperClassName = '',
  EFOProduct = false,
  enableColorSwatches = false,
  imageClassName,
  isLazyLoading = true,
  isNew = false,
  isStorePickup,
  lineItemPrice,
  lineItemPriceClassName,
  listPrice,
  marketplaceProduct = false,
  name,
  onCardClick,
  onImageError,
  onImageLoad,
  onlineExclusive = false,
  productId,
  productImage,
  productLinkTitle,
  productURL,
  promoMessage,
  quantity,
  quantityClassName,
  rating = 0,
  salePrice,
  scale = false,
  sellerName,
  sizeClassName,
  sizeName,
  titleClassName,
  unitPriceClassName,
  hideColorSwatch = false,
}: HorizontalProductCardProps) => {
  return (
    <div className={cn(s.productHContainer, className)}>
      <Image
        className={cn(s.productImage, imageClassName)}
        alt={name}
        isLazyLoading={isLazyLoading}
        onError={onImageError}
        onImageClick={onCardClick}
        onLoad={onImageLoad}
        responsive
        scale={scale}
        src={productImage}
      />
      <div className={cn(s.productHDescription, descriptionWrapperClassName)}>
        <ProductColors
          additionalColorsLink={additionalColorsLink || productURL}
          colorSwatches={colors.colorSwatches}
          displayColorSwatches={enableColorSwatches}
          onChangeColor={colors.onChangeColor}
          className={s.productColors}
          selectedSkuUpc={colors.selectedSkuUpc}
          productId={productId}
          hideColorSwatch={hideColorSwatch}
        />
        {customText}
        <ProductTag
          EFOProduct={EFOProduct}
          isNew={isNew}
          marketplaceProduct={marketplaceProduct}
          onlineExclusive={onlineExclusive}
        />
        <ProductTitle
          canUseRouter={canUseRouter} // we need this to ensure that React Router is never used in raven-header
          className={cn(s.productTitle, titleClassName)}
          name={name}
          onCardClick={onCardClick}
          productId={productId}
          productLinkTitle={productLinkTitle}
          productURL={productURL}
        />
        <ProductSku
          className={s.productSkuDetails}
          color={colorName}
          colorClassName={colorClassName}
          size={sizeName}
          sizeClassName={sizeClassName}
        />
        {listPrice && (
          <div className={s.productPrice}>
            <Price
              className={unitPriceClassName}
              layout="stacked"
              listPrice={listPrice}
              promoMessage={promoMessage}
              promoMessageContainerClassName={s.promoMessageContainer}
              salePrice={salePrice}
            />
          </div>
        )}
        {quantity && lineItemPrice && (
          <div className={s.lineItemDetails}>
            <ProductDetail
              className={quantityClassName}
              detailKey="QTY"
              detailValue={quantity}
            />
            <p className={cn(s.lineItemPriceWrapper, lineItemPriceClassName)}>
              <strong className={s.lineItemPrice}>{lineItemPrice}</strong>
            </p>
          </div>
        )}
        {sellerName && (
          <ProductDetail
            detailKey="Sold by"
            detailValue={sellerName}
            className={s.detailContainer}
          />
        )}
        {rating > 0 && (
          <StarRating
            rating={rating}
            size="large"
            className={s.ratingContainer}
          />
        )}
        <ProductShipping isStorePickup={isStorePickup} />
        {children}
      </div>
    </div>
  );
};

export default HorizontalProductCard;
